import { Fragment, useState, useEffect, useRef } from 'react';
import { Menu, Popover, Transition } from '@headlessui/react';
import { Bars3Icon, BellIcon, XMarkIcon, DocumentIcon, CheckCircleIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import DocumentUploader, { addOwnerButtonRef } from './documentTracker';
import FormEditor from './FormEditor';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, query, where, getDocs, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import ChatBot, { documentRequirements } from './chat';
import logo from '../images/images.png';
import 'intro.js/introjs.css';
import introJs from 'intro.js';
import TermSheetViewer from './TermSheetViewer';
import ApplicationView from './ApplicationView';
import DealRoom from './DealRoom';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import Prequalification from './Prequalification';
import LenderMatch from './LenderMatch';
import { useNavigate, Link } from 'react-router-dom';

const user = {
  name: 'Tom Cook',
  email: 'tom@example.com',
  imageUrl:
    'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
};

const navigation = [
  { id: 'currentTask', name: 'Document Upload' },
  { id: 'termSheets', name: 'Term Sheets' },

];

const userNavigation = [
  { name: 'Your Profile', href: '#' },
  { name: 'Settings', href: '#' },
  { name: 'Sign out', href: '#' },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function Example() {
  const [selectedItem, setSelectedItem] = useState('currentTask');
  const [currentUser, setCurrentUser] = useState(null);
  const [isChatbotExpanded, setIsChatbotExpanded] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [tutorialStarted, setTutorialStarted] = useState(false);
  const [currentLoanId, setCurrentLoanId] = useState(null);
  const addOwnerButtonRef = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [loanType, setLoanType] = useState(null);
  const [personalGuarantors, setPersonalGuarantors] = useState([]);
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [formData, setFormData] = useState({});
  const [selectedDocument, setSelectedDocument] = useState(null);
  const navigate = useNavigate();
  const documentTrackerRef = useRef(null);
  const [editMode, setEditMode] = useState(false);

  const startTutorial = () => {
    const intro = introJs();
    intro.setOptions({
      steps: [
        {
          element: '#documentUploader',
          intro: 'This is where you can upload the required documents for your loan application.',
        },
        {
          element: addOwnerButtonRef.current,
          intro: 'Click here to add additional owners to your loan application.',
        },
        {
          element: '#startApplicationButton',
          intro: 'Once you have uploaded all the necessary documents, click here to start your loan application.',
        },
      ],
    });
    intro.start();
  };

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      if (currentUser) {
        try {
          const db = getFirestore();
          const formQuery = query(collection(db, 'loanApplications'), where('userId', '==', currentUser.uid));
          
          const unsubscribeForm = onSnapshot(formQuery, (querySnapshot) => {
            if (!querySnapshot.empty) {
              const formDoc = querySnapshot.docs[0];
              const fetchedFormData = formDoc.data();
              setFormData(fetchedFormData);
              const capitalizedFirstName = fetchedFormData.firstName.charAt(0).toUpperCase() + fetchedFormData.firstName.slice(1);
              setFirstName(capitalizedFirstName);
              setCurrentLoanId(formDoc.id);
              setLoanType(fetchedFormData.loanPurpose);
              
              if (fetchedFormData.personalGuarantors) {
                setPersonalGuarantors(fetchedFormData.personalGuarantors);
              }
            }
          });

          // If we have a loan ID, set up real-time document updates
          if (currentLoanId) {
            const documentsQuery = query(
              collection(db, 'documentMetadata'),
              where('userId', '==', currentUser.uid)
            );

            const unsubscribeDocuments = onSnapshot(documentsQuery, async (snapshot) => {
              const documentPromises = snapshot.docs.map(async (doc) => {
                const documentData = doc.data();
                const ocrMetadataQuery = query(
                  collection(db, 'OCRMetadata'),
                  where('userId', '==', currentUser.uid),
                  where('documentId', '==', doc.id)
                );
                const ocrMetadataSnapshot = await getDocs(ocrMetadataQuery);
                const ocrData = ocrMetadataSnapshot.docs[0]?.data();
                return {
                  id: doc.id,
                  ...documentData,
                  ocrResult: ocrData?.ocrResult || '',
                };
              });

              const documents = await Promise.all(documentPromises);
              setUploadedDocuments(documents);
            });

            return () => {
              unsubscribeDocuments();
              unsubscribeForm();
            };
          }

          return () => {
            unsubscribeForm();
          };
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      }
    };

    fetchUserData();
  }, [currentUser, currentLoanId]);

  useEffect(() => {
    if (addOwnerButtonRef.current) {
      if (!tutorialStarted) {
        startTutorial();
        setTutorialStarted(true);
      }
    }
  }, [addOwnerButtonRef, tutorialStarted]);

  const handleFormDataChange = (formData) => {
    const capitalizedFirstName = formData.firstName.charAt(0).toUpperCase() + formData.firstName.slice(1);
    setFirstName(capitalizedFirstName);
  };
  
  const handleNavigationClick = (itemId) => {
    setSelectedItem(itemId);
  };

  const toggleChatbotExpanded = () => {
    setIsChatbotExpanded(!isChatbotExpanded);
  };

  const handleReviewSubmit = () => {
    setSelectedItem('applicationView');
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  // Filter navigation items based on loan type
  const filteredNavigation = navigation.filter(item => 
    !['workingCapital-nonsba', 'workingCapital'].includes(loanType) || item.id !== 'applicationView'
  );

  // Update getNextRequiredDocuments to properly check personal documents
  const getNextRequiredDocuments = () => {
    if (!formData || !personalGuarantors || !uploadedDocuments) return [];
    
    const missingDocuments = [];
    
    documentRequirements.forEach(requirement => {
      if (requirement.Individual) {
        // For personal documents, check each guarantor
        personalGuarantors.forEach(guarantor => {
          const guarantorName = `${guarantor.firstName} ${guarantor.lastName}`;
          
          // Special handling for credit scores - skip if already uploaded
          if (requirement.Name === "Credit Score") {
            const hasScore = formData?.creditScores?.[guarantorName];
            if (!hasScore) {
              missingDocuments.push(`${requirement.Name} (${guarantorName})`);
            }
            return; // Skip to next iteration if credit score exists
          }
          
          // Special handling for tax returns to check each year
          if (requirement.Name === "Personal Tax Returns") {
            requirement.Labels.forEach(label => {
              const hasDocument = uploadedDocuments.some(doc => 
                doc.documentName === requirement.Name && 
                doc.guarantorId === guarantor.id &&
                doc.label === label
              );
              if (!hasDocument) {
                missingDocuments.push(`${requirement.Name} - ${label} (${guarantorName})`);
              }
            });
          } else {
            // For other personal documents
            const hasDocument = uploadedDocuments.some(doc => 
              doc.documentName === requirement.Name && 
              doc.guarantorId === guarantor.id
            );
            if (!hasDocument) {
              missingDocuments.push(`${requirement.Name} (${guarantorName})`);
            }
          }
        });
      } else {
        // For business documents
        requirement.Labels.forEach(label => {
          const hasDocument = uploadedDocuments.some(
            doc => doc.documentName === requirement.Name &&
                  doc.label === label
          );
          if (!hasDocument) {
            missingDocuments.push(`${requirement.Name} - ${label}`);
          }
        });
      }
    });
    
    return missingDocuments;
  };

  // Add this helper function to get status color
  const getStatusColor = (docName) => {
    const baseDocName = docName.split(' (')[0];
    const label = docName.split(' - ')[1];
    
    if (baseDocName === 'Credit Score') {
      const guarantorName = docName.match(/\((.*?)\)/)?.[1];
      return formData?.creditScores?.[guarantorName] ? {
        bg: 'bg-green-50',
        text: 'text-green-700',
        icon: 'text-green-400',
        border: 'border-green-200'
      } : {
        bg: 'bg-yellow-50',
        text: 'text-yellow-700',
        icon: 'text-yellow-400',
        border: 'border-yellow-200'
      };
    }
    
    // Check for specific tax return years
    if (baseDocName.includes('Tax Return')) {
      const hasDocument = uploadedDocuments.some(doc => 
        doc.documentName === baseDocName && 
        doc.label === label
      );
      
      return hasDocument ? {
        bg: 'bg-green-50',
        text: 'text-green-700',
        icon: 'text-green-400',
        border: 'border-green-200'
      } : {
        bg: 'bg-yellow-50',
        text: 'text-yellow-700',
        icon: 'text-yellow-400',
        border: 'border-yellow-200'
      };
    }
    
    // For other documents
    return uploadedDocuments.some(doc => doc.documentName === baseDocName) ? {
      bg: 'bg-green-50',
      text: 'text-green-700',
      icon: 'text-green-400',
      border: 'border-green-200'
    } : {
      bg: 'bg-yellow-50',
      text: 'text-yellow-700',
      icon: 'text-yellow-400',
      border: 'border-yellow-200'
    };
  };

  // Helper function to group documents
  const groupDocuments = (documents) => {
    const personal = [];
    const business = [];

    documents.forEach(doc => {
      if (doc.includes('(')) {
        // Documents with guarantor names are personal
        personal.push(doc);
      } else {
        // Documents without guarantor names are business
        business.push(doc);
      }
    });

    return { personal, business };
  };

  // Fix the document click handler to properly navigate
  const handleDocumentClick = (document) => {
    // Navigate to the section first
    setSelectedItem('currentTask');
    
    // Find the matching document
    const docName = document.name.split(' - ')[0];
    const matchingDoc = documentRequirements.find(req => req.Name === docName);
    
    if (matchingDoc) {
      // Small delay to ensure DocumentTracker is mounted
      setTimeout(() => {
        if (documentTrackerRef.current) {
          documentTrackerRef.current.setSelectedDocument(matchingDoc);
          documentTrackerRef.current.handleReviewClick(matchingDoc);
        }
      }, 100);
    }
  };

  const handleLoanApplicationClick = () => {
    if (formData?.loanSize) {
      setEditMode(true);
      setSelectedItem('currentTask');
    } else {
      navigate('/loanform');
    }
  };

  const handleSave = async (updatedFormData) => {
    // Save the form data
    try {
      const db = getFirestore();
      const formRef = doc(db, 'loanApplications', currentLoanId);
      await updateDoc(formRef, updatedFormData);
      setEditMode(false);
    } catch (error) {
      console.error('Error saving form data:', error);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
  };

  return (
    <div className="min-h-full">
      <Popover as="header" className="bg-blue-900 pb-24">
        {({ open }) => (
          <>
            <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:max-w-7xl lg:px-8">
              <div className="relative flex items-center justify-center py-5 lg:justify-between">
                <div className="absolute left-0 flex-shrink-0 lg:static">
                  <a href="#">
                    <span className="sr-only">Your Company</span>
                    <img
                      className="h-8 w-auto"
                      src={logo}
                      alt="Your Company"
                    />
                  </a>
                </div>

                <div className="absolute right-0 top-5 flex-shrink-0 lg:hidden">
                  <Menu as="div" className="relative inline-block text-left">
                    <div>
                      <Menu.Button className="inline-flex w-full justify-center items-center gap-x-1.5 rounded-md bg-blue-800 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-blue-900 focus:ring-white">
                        {navigation.find(item => item.id === selectedItem)?.name}
                        <ChevronDownIcon className="-mr-1 h-5 w-5" aria-hidden="true" />
                      </Menu.Button>
                    </div>

                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="absolute right-0 z-50 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                        <div className="py-1">
                          {navigation.map((item) => (
                            <Menu.Item key={item.id}>
                              {({ active }) => (
                                <button
                                  onClick={() => handleNavigationClick(item.id)}
                                  className={classNames(
                                    active ? 'bg-blue-100 text-blue-900' : 'text-gray-700',
                                    'block px-4 py-2 text-sm w-full text-left'
                                  )}
                                >
                                  {item.name}
                                </button>
                              )}
                            </Menu.Item>
                          ))}
                        </div>
                      </Menu.Items>
                    </Transition>
                  </Menu>
                </div>
              </div>
              <div className="hidden border-t border-white border-opacity-20 py-5 lg:block">
                <div className="grid grid-cols-3 items-center gap-8">
                  <div className="col-span-2">
                    <nav className="flex space-x-4">
                      {filteredNavigation.map((item) => (
                        <button
                          key={item.id}
                          onClick={() => handleNavigationClick(item.id)}
                          className={classNames(
                            selectedItem === item.id ? 'text-white' : 'text-indigo-100',
                            'rounded-md bg-white bg-opacity-0 px-3 py-2 text-sm font-medium hover:bg-opacity-10'
                          )}
                          aria-current={selectedItem === item.id ? 'page' : undefined}
                        >
                          {item.name}
                        </button>
                      ))}
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </Popover>
      <main className="-mt-24 pb-8">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:max-w-7xl lg:px-8">
          <h1 className="sr-only">Page title</h1>
          <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-3 lg:gap-8">
            <div className={`grid grid-cols-1 gap-4 ${
              selectedItem === 'termSheets' || 
              selectedItem === 'applicationView' || 
              selectedItem === 'dealRoom' || 
              selectedItem === 'lenderMatch' ||
              selectedItem === 'prequalification' ? 'lg:col-span-3' : 
              isChatbotExpanded ? 'lg:col-span-1' : 'lg:col-span-2'
            }`}>
              <section aria-labelledby="section-1-title">
                <h2 className="sr-only" id="section-1-title">
                  Section title
                </h2>
                <div className="overflow-hidden rounded-lg bg-white shadow">
                  <div className={selectedItem === 'applicationView' ? '' : 'p-6'}>
                    {selectedItem === 'prequalification' ? (
                      <div id="prequalification">
                        <Prequalification
                          currentUser={currentUser}
                          personalGuarantors={personalGuarantors}
                          uploadedDocuments={uploadedDocuments}
                          onFileChange={(file, guarantorId, documentName, label) => {
                            // Implement file upload logic here
                            console.log('File upload:', file, guarantorId, documentName, label);
                          }}
                          loanPurpose={loanType}
                          formData={formData}
                        />
                      </div>
                    ) : selectedItem === 'currentTask' ? (
                      editMode ? (
                        <FormEditor
                          formData={formData}
                          handleChange={(e) => {
                            const { name, value } = e.target;
                            setFormData(prev => ({
                              ...prev,
                              [name]: value
                            }));
                          }}
                          handleSave={() => handleSave(formData)}
                          handleCancel={handleCancel}
                          handleCapTableChange={(index, field, value) => {
                            const newCapTable = [...formData.capTable];
                            newCapTable[index][field] = value;
                            setFormData(prev => ({
                              ...prev,
                              capTable: newCapTable
                            }));
                          }}
                          handleAddCapTableRow={() => {
                            setFormData(prev => ({
                              ...prev,
                              capTable: [...(prev.capTable || []), { name: '', percentage: '' }]
                            }));
                          }}
                          handleDeleteCapTableRow={(index) => {
                            setFormData(prev => ({
                              ...prev,
                              capTable: prev.capTable.filter((_, i) => i !== index)
                            }));
                          }}
                        />
                      ) : (
                        <div id="documentUploader">
                          <DocumentUploader
                            ref={documentTrackerRef}
                            onFormDataChange={(data) => {
                              // Handle form data changes
                              console.log('Form data changed:', data);
                            }}
                            selectedDocument={selectedDocument}
                            setSelectedDocument={setSelectedDocument}
                            currentUser={currentUser}
                            personalGuarantors={personalGuarantors}
                            loanType={loanType}
                            currentLoanId={currentLoanId}
                          />
                        </div>
                      )
                    ) : selectedItem === 'termSheets' ? (
                      <div>
                        {currentLoanId ? (
                          <TermSheetViewer loanId={currentLoanId} />
                        ) : (
                          <p className="mt-1 text-sm text-gray-500">No active loan application found.</p>
                        )}
                      </div>
                    ) : selectedItem === 'applicationView' && !['workingCapital-nonsba', 'workingCapital'].includes(loanType) ? (
                      <div>
                        {currentLoanId ? (
                          <ApplicationView loanId={currentLoanId} />
                        ) : (
                          <p className="mt-1 text-sm text-gray-500">No active loan application found.</p>
                        )}
                      </div>
                    ) : selectedItem === 'dealRoom' ? (
                      <DealRoom />
                    ) : selectedItem === 'lenderMatch' ? (
                      <LenderMatch />
                    ) : selectedItem === 'prequalification' ? (
                      <Prequalification />
                    ) : null}
                  </div>
                </div>
              </section>
            </div>
            {selectedItem !== 'termSheets' && selectedItem !== 'applicationView' && selectedItem !== 'dealRoom' && selectedItem !== 'lenderMatch' && selectedItem !== 'prequalification' && (
              <div className={`grid grid-cols-1 gap-4 ${isChatbotExpanded ? 'lg:col-span-2' : 'lg:col-span-1'}`}>
                <section aria-labelledby="section-2-title">
                  <h2 className="sr-only" id="section-2-title">
                    Section title
                  </h2>
                  <div className="overflow-hidden rounded-lg bg-white shadow">
                    <ChatBot
                      firstName={firstName}
                      toggleChatbotExpanded={toggleChatbotExpanded}
                      isChatbotExpanded={isChatbotExpanded}
                    />
                  </div>
                </section>
              </div>
            )}
          </div>
        </div>
      </main>
      
      {/* Add this floating button before the closing div */}
      <div className="fixed bottom-4 right-4 z-50">
        <Menu as="div" className="relative inline-block text-left">
          <Menu.Button className="rounded-full bg-blue-600 p-4 text-white shadow-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
            <span className="flex items-center">
              What's Next?
              <ChevronDownIcon className="-mr-1 ml-2 h-5 w-5" aria-hidden="true" />
            </span>
          </Menu.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute bottom-full right-0 mb-2 w-96 rounded-md bg-white shadow-xl ring-1 ring-black ring-opacity-5 focus:outline-none">
              <div className="p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center sticky top-0 bg-white">
                  <DocumentIcon className="h-5 w-5 mr-2 text-blue-500" />
                  Next Steps
                </h3>
                <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                  {/* Personal Documents Section */}
                  {groupDocuments(getNextRequiredDocuments()).personal.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-3 border-b pb-2">
                        Personal Documents
                      </h4>
                      <div className="space-y-2">
                        {groupDocuments(getNextRequiredDocuments()).personal.map((doc, index) => {
                          const colors = getStatusColor(doc);
                          const docName = doc.split(' (')[0];
                          const guarantorName = doc.match(/\((.*?)\)/)?.[1];
                          
                          return (
                            <div
                              key={`${doc}-${index}`}
                              className={`w-full flex items-center p-2 rounded-lg ${colors.bg} border ${colors.border}`}
                            >
                              <div className="flex-shrink-0">
                                <DocumentIcon className={`h-5 w-5 ${colors.icon}`} />
                              </div>
                              <div className="ml-3 flex-1">
                                <p className={`text-sm font-medium ${colors.text}`}>
                                  {docName}
                                </p>
                                {guarantorName && (
                                  <p className={`text-xs mt-0.5 ${colors.text} opacity-75`}>
                                    For guarantor: {guarantorName}
                                  </p>
                                )}
                              </div>
                              <div className="ml-2">
                                <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${colors.bg} ${colors.text}`}>
                                  Required
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Loan Application Section */}
                  {!formData.loanApplicationComplete && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-3 border-b pb-2">
                        Loan Application
                      </h4>
                      <button
                        onClick={handleLoanApplicationClick}
                        className="w-full flex items-center p-2 rounded-lg bg-blue-50 border border-blue-200 hover:bg-blue-100 transition-colors"
                      >
                        <div className="flex-shrink-0">
                          <DocumentTextIcon className="h-5 w-5 text-blue-400" />
                        </div>
                        <div className="ml-3 flex-1 text-left">
                          <p className="text-sm font-medium text-blue-700">
                            Complete Loan Application
                          </p>
                          <p className="text-xs mt-0.5 text-blue-600 opacity-75">
                            {formData?.loanSize ? 'Review application details' : 'Start your application'}
                          </p>
                        </div>
                        <div className="ml-2">
                          <span className="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium bg-blue-50 text-blue-700">
                            Required
                          </span>
                        </div>
                      </button>
                    </div>
                  )}

                  {/* Business Documents Section */}
                  {groupDocuments(getNextRequiredDocuments()).business.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-3 border-b pb-2">
                        Business Documents
                      </h4>
                      <div className="space-y-2">
                        {groupDocuments(getNextRequiredDocuments()).business.map((doc, index) => {
                          const colors = getStatusColor(doc);
                          const docName = doc.split(' - ')[0];
                          
                          return (
                            <div
                              key={`${doc}-${index}`}
                              className={`w-full flex items-center p-2 rounded-lg ${colors.bg} border ${colors.border}`}
                            >
                              <div className="flex-shrink-0">
                                <DocumentIcon className={`h-5 w-5 ${colors.icon}`} />
                              </div>
                              <div className="ml-3 flex-1">
                                <p className={`text-sm font-medium ${colors.text}`}>
                                  {docName}
                                </p>
                                {doc.split(' - ')[1] && (
                                  <p className={`text-xs mt-0.5 ${colors.text} opacity-75`}>
                                    {doc.split(' - ')[1]}
                                  </p>
                                )}
                              </div>
                              <div className="ml-2">
                                <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${colors.bg} ${colors.text}`}>
                                  Required
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Menu.Items>
          </Transition>
        </Menu>
      </div>
    </div>
  );
}
